import { getProperty, setProperty } from "dot-prop";
import {
  createEffect,
  Signal,
  getOwner,
  runWithOwner,
  createMemo,
  createSignal,
  Setter,
  on,
} from "solid-js";
import type { MemoOptions } from "solid-js/types/reactive/signal";

import { Get } from "type-fest";

export type MapOptions<T, U> = {
  from(value: T): U;
  into(value: U): T;
} & MemoOptions<U>;

export const deepSignal = <P extends string, T extends Record<string, unknown>>(
  [source, setSource]: Signal<T>,
  path: P,
  options?: MemoOptions<Get<T, P> | undefined>
): Signal<Get<T, P> | undefined> => [
  createMemo(() => getProperty(source(), path)),
  function () {
    let d = arguments.length === 0 ? undefined : arguments[0];
    let newValue: any;
    setSource((prev) => {
      const oldValue = getProperty(prev, path);
      newValue = typeof d === "function" ? d(oldValue) : d;
      const changed =
        options?.equals === false ||
        (options?.equals?.(oldValue, newValue) ?? oldValue === newValue) ==
          false;
      return changed
        ? setProperty(
            (Array.isArray(prev) ? [...prev] : { ...prev }) as T,
            path,
            newValue
          )
        : prev;
    });
    return newValue;
  },
];

export const mapSignal = <T, U>(
  [source, setSource]: Signal<T>,
  { from, into, equals }: MapOptions<T, U>
): Signal<U> => {
  const accessor = createMemo(() => from(source()), { equals });
  const setter = ((value) => {
    const result = setSource((prev) => {
      const oldValue = from(prev);
      // @ts-expect-error
      const newValue = typeof value === "function" ? value(oldValue) : value;
      const updated =
        equals === false ||
        (equals?.(oldValue, newValue) ?? oldValue === newValue) === false;
      return updated ? into(newValue) : prev;
    });
    return from(result);
  }) as Setter<U>;
  return [accessor, setter];
};

export const createAsyncMemo = <T, U>(
  source: () => U,
  resolver: (input: U) => Promise<T>
) => {
  const [pending, setLoading] = createSignal(false);
  const [error, setError] = createSignal<unknown>();
  const [latest, setLatest] = createSignal<T | undefined>(undefined);
  const result = createMemo(
    on([pending, error, latest], ([loading, error, latest]) =>
      loading === false && error === undefined ? latest : undefined
    )
  );
  createEffect(() => {
    setLoading(true);
    resolver(source())
      .then(setLatest)
      .catch(setError)
      .finally(() => setLoading(false));
  });
  return { result, latest, error, pending };
};

export const withLock =
  <T, F extends (...args: any) => T | Promise<T>>(
    acquire: Setter<boolean>,
    fn: F
  ) =>
  async (...args: Parameters<F>) => {
    const lock = acquireLock(acquire);
    if (lock === undefined) {
      throw new Error("already locked");
    }
    try {
      return await Promise.resolve(fn.call(undefined, args));
    } finally {
      lock.release();
    }
  };

export const acquireLock = (set: Setter<boolean>) => {
  let locked = false;
  set((prev) => {
    locked = prev;
    return true;
  });
  return locked
    ? undefined
    : {
        release() {
          set(false);
        },
      };
};

// https://github.com/solidjs/solid-router/blob/c167cd8e2d9afca91f30bf83bbadde6687feaae1/src/utils.ts#L98
export function createMemoObject<T extends Record<string | symbol, unknown>>(
  fn: () => T
): T {
  const map = new Map();
  const owner = getOwner()!;
  return new Proxy({} as T, {
    get(_, property) {
      if (!map.has(property)) {
        runWithOwner(owner, () => {
          map.set(
            property,
            createMemo(() => fn()[property])
          );
        });
      }
      return map.get(property)();
    },
    getOwnPropertyDescriptor() {
      return {
        enumerable: true,
        configurable: true,
      };
    },
    ownKeys() {
      return Reflect.ownKeys(fn());
    },
  });
}
